import type { ModuleEvidenceContract } from './moduleEvidenceContract';
import { defineModuleEvidenceContract, defineModuleEvidenceEntry } from './moduleEvidenceContract';
import { DECISION_AGENT_SOURCE_CLASSES } from './sourceClasses';

// #4449 batch 3 (2026-08-09) — the single-snapshot lever modules.
//
// capacity-utilization, fuel-efficiency, marketing-spend, and network-optimization
// are structurally identical to each other and to segment-margin-cleanup (#4449
// batch 2), so they are declared once here through a shared factory rather than
// copied into four near-identical files. Each one:
//
//   - exposes exactly ONE evidence key to its intents, `approved_<module>_snapshot`,
//     declared in governedCapability.requiredEvidence with sourceClass
//     `manual-admin`, freshness `maxAgeHours: 2160` (90 days), staleBehavior block,
//     and requiredEvidenceDomains ['custom'];
//   - reads it from `decision_agent:<module>:governed_snapshot` at
//     `<module>_governed_snapshot_v1` — the module's own
//     <MODULE>_DATA_LAKE_CONTEXT_KIND / _SCHEMA_VERSION literals;
//   - additionally declares nine requiredState keys (the *_attested / *_governed
//     pairs and three hyphenated snapshot registers). Those are a separate
//     attested/governed substrate, NOT resolved through any
//     AskHelixMemoryArtifact contextKind, so none of them is registered here —
//     the same scoping #4521 applied to org-analysis-optimization's base keys.
//
// Confirmed live on tenant helix_retail 2026-08-09 (read-only), every module
// carrying APPROVED, ingest-anchored rows under the pair registered below:
//
//   capacity-utilization    4 rows   approvedAt 2026-07-29   asOf 2026-07-05
//   fuel-efficiency         5 rows   approvedAt 2026-07-29   asOf 2026-07-05
//   marketing-spend         6 rows   approvedAt 2026-07-29   asOf 2026-07-05
//   network-optimization    5 rows   approvedAt 2026-07-29   asOf 2026-07-05
//
// CADENCE (deliberately stricter than the modules ask, same reasoning as
// segment-margin-cleanup): the modules allow 90 days; this substrate expresses
// freshness as a cadence and none equals 90 days (monthly is 45, quarterly 135).
// `monthly` is registered so a contract can never report evidence fresher than
// the module itself would accept. At registration the live asOf is 35 days old:
// PRESENT, with roughly ten days of headroom.
const LEVER_SNAPSHOT_MODULES = [
  {
    moduleId: 'capacity-utilization',
    evidenceKey: 'approved_capacity_utilization_snapshot',
    contextKind: 'decision_agent:capacity_utilization:governed_snapshot',
    schemaVersion: 'capacity_utilization_governed_snapshot_v1',
    description:
      'Approved capacity-utilization governed snapshot; carries utilised versus available ' +
      'capacity with its addressable share, and backs every capacity read and scenario.',
  },
  {
    moduleId: 'fuel-efficiency',
    evidenceKey: 'approved_fuel_efficiency_snapshot',
    contextKind: 'decision_agent:fuel_efficiency:governed_snapshot',
    schemaVersion: 'fuel_efficiency_governed_snapshot_v1',
    description:
      'Approved fuel-efficiency governed snapshot; carries fuel spend and consumption against ' +
      'the addressable base, and backs every fuel read and abatement scenario.',
  },
  {
    moduleId: 'marketing-spend',
    evidenceKey: 'approved_marketing_spend_snapshot',
    contextKind: 'decision_agent:marketing_spend:governed_snapshot',
    schemaVersion: 'marketing_spend_governed_snapshot_v1',
    description:
      'Approved marketing-spend governed snapshot; carries marketing spend with its addressable ' +
      'and program decomposition, and backs every marketing efficiency read and scenario.',
  },
  {
    moduleId: 'network-optimization',
    evidenceKey: 'approved_network_optimization_snapshot',
    contextKind: 'decision_agent:network_optimization:governed_snapshot',
    schemaVersion: 'network_optimization_governed_snapshot_v1',
    description:
      'Approved network-optimization governed snapshot; carries network cost and flow against ' +
      'the addressable base, and backs every reconfiguration read and scenario.',
  },
] as const;

const NO_LEVER_SNAPSHOT_BINDING_PLACEHOLDERS: readonly string[] = Object.freeze([]);

function defineLeverSnapshotContract(spec: (typeof LEVER_SNAPSHOT_MODULES)[number]) {
  const entry = defineModuleEvidenceEntry(spec.evidenceKey, {
    sourceClass: DECISION_AGENT_SOURCE_CLASSES.manualAdmin,
    contextKind: spec.contextKind,
    schemaVersion: spec.schemaVersion,
    bindingPlaceholders: NO_LEVER_SNAPSHOT_BINDING_PLACEHOLDERS,
    domain: 'custom',
    freshnessCadence: 'monthly',
    requiredFor: ['monitoring', 'scenario', 'decision_pack', 'ask_helix'],
    missingBehavior: 'block',
    description: spec.description,
  });

  return defineModuleEvidenceContract(
    spec.moduleId,
    { governed_snapshot: entry },
    {
      evidenceKeys: [spec.evidenceKey],
      sourceClasses: [DECISION_AGENT_SOURCE_CLASSES.manualAdmin],
      contextKinds: [spec.contextKind],
      schemaVersions: [spec.schemaVersion],
      stateKeys: [spec.evidenceKey],
      bindingPlaceholders: NO_LEVER_SNAPSHOT_BINDING_PLACEHOLDERS,
    },
  );
}

export const CAPACITY_UTILIZATION_MODULE_EVIDENCE_CONTRACT = defineLeverSnapshotContract(
  LEVER_SNAPSHOT_MODULES[0],
);
export const FUEL_EFFICIENCY_MODULE_EVIDENCE_CONTRACT = defineLeverSnapshotContract(
  LEVER_SNAPSHOT_MODULES[1],
);
export const MARKETING_SPEND_MODULE_EVIDENCE_CONTRACT = defineLeverSnapshotContract(
  LEVER_SNAPSHOT_MODULES[2],
);
export const NETWORK_OPTIMIZATION_MODULE_EVIDENCE_CONTRACT = defineLeverSnapshotContract(
  LEVER_SNAPSHOT_MODULES[3],
);

/** Every batch-3 lever-snapshot contract, in registration order. */
export const LEVER_SNAPSHOT_MODULE_EVIDENCE_CONTRACTS: readonly ModuleEvidenceContract[] =
  Object.freeze([
    CAPACITY_UTILIZATION_MODULE_EVIDENCE_CONTRACT,
    FUEL_EFFICIENCY_MODULE_EVIDENCE_CONTRACT,
    MARKETING_SPEND_MODULE_EVIDENCE_CONTRACT,
    NETWORK_OPTIMIZATION_MODULE_EVIDENCE_CONTRACT,
  ]);
